<template>
  <v-navigation-drawer expand-on-hover permanent rail app v-model:rail="isRail" color="primary" class="menu-drawer"
    :rail-width="70">
    <v-list nav density="comfortable" class="menu-list">

      <!-- Dashboard -->
      <!--<v-list-item title="Painel de Manobras" dense clickable @click="onChildClick({ value: '/painel-manobras' })"
        :class="['cursor-pointer', { 'active-item': isActive('/painel-manobras') }]">
        <template #prepend>
          <v-icon icon="fas fa-grip" class="group-icon" />
        </template>
</v-list-item>

<v-divider class="mx-2 my-2" />-->

      <!-- BUSCA (visível apenas quando expandido) -->
      <v-slide-y-transition>
        <div class="search-wrapper mb-2">
          <v-text-field v-model="busca" density="compact" variant="outlined" placeholder="Buscar no menu..."
            hide-details clearable class="search-field" @click.stop>
            <template #prepend-inner>
              <font-awesome-icon icon="magnifying-glass" class="search-icon" />
            </template>
          </v-text-field>
        </div>
      </v-slide-y-transition>

      <!-- Sem resultado -->
      <v-list-item v-if="!isRail && busca && menuFiltrado.length === 0" dense>
        <v-list-item-title class="text-caption text-disabled text-center">
          Nenhum item encontrado
        </v-list-item-title>
      </v-list-item>

      <!-- MÓDULOS DINÂMICOS, FILTRADOS POR PERMISSÕES -->
      <template v-for="(mod, idx) in menuFiltrado" :key="mod.title">
        <!-- Se houver subitens, exibe grupo expansível -->
        <template v-if="mod.children?.length">
          <v-list-item dense clickable @click="toggleGroup(idx)"
            :class="['group-item', { 'active-group': isGroupActive(idx) }]">
            <template #prepend>
              <v-icon :icon="mod.icon" class="group-icon" />
            </template>
            <v-list-item-title>{{ mod.title }}</v-list-item-title>
            <template #append>
              <v-icon icon="fas fa-chevron-down" class="expand-icon"
                :class="{ rotate: openedGroup === idx || (busca && mod._matchBusca) }" />
            </template>
          </v-list-item>

          <v-expand-transition>
            <div v-show="openedGroup === idx || (busca && mod._matchBusca)" class="subitem-wrapper">
              <v-list-item v-for="child in mod.children" :key="child.value" dense clickable @click="onChildClick(child)"
                :class="['cursor-pointer', 'subitem', { 'active-item': isActive(child.value), 'subitem-highlight': busca && child._match }]">
                <template #title>
                  <span v-if="busca && child._match" v-html="highlightText(child.title, busca)" />
                  <span v-else>{{ child.title }}</span>
                </template>
              </v-list-item>
            </div>
          </v-expand-transition>
        </template>

        <!-- Se não tiver subitens, clicável direto para a URL do módulo -->
        <template v-else>
          <v-list-item dense clickable @click="onChildClick({ value: mod.value })"
            :class="['cursor-pointer', { 'active-item': isActive(mod.value) }]">
            <template #prepend>
              <v-icon :icon="mod.icon" class="group-icon" />
            </template>
            <v-list-item-title>
              <span v-if="busca && mod._match" v-html="highlightText(mod.title, busca)" />
              <span v-else>{{ mod.title }}</span>
            </v-list-item-title>
          </v-list-item>
        </template>
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserMenu } from '@/composables/useUserMenu'

// Menu dinâmico
const { filteredMenu } = useUserMenu()

const router = useRouter()
const route = useRoute()

// Estado rail e grupos
const isRail = ref(true)
const openedGroup = ref(-1)

// Verifica se rota é ativa
function isActive(path) {
  return route.path === path || route.path.startsWith(path + '/')
}

// Alterna expansão de grupo
function toggleGroup(idx) {
  openedGroup.value = openedGroup.value === idx ? -1 : idx
}

// Navega e fecha grupo
function onChildClick(item) {
  openedGroup.value = -1
  busca.value = ''
  if (item.value) router.push(item.value)
}

// Índice de grupo ativo
const activeGroupIndex = computed(() =>
  filteredMenu.value.findIndex(mod =>
  (mod.children?.length
    ? mod.children.some(child => isActive(child.value))
    : mod.value === route.path)
  )
)

// Verifica se grupo está ativo
function isGroupActive(idx) {
  return openedGroup.value === idx || activeGroupIndex.value === idx
}

const busca = ref('')

// Limpa busca ao colapsar o drawer
watch(isRail, (val) => { if (val) busca.value = '' })

// Limpa busca ao navegar
watch(() => route.path, () => { busca.value = '' })

// Menu filtrado
const menuFiltrado = computed(() => {
  const termo = busca.value?.trim().toLowerCase()
  if (!termo) return filteredMenu.value
  const resultado = []
  for (const mod of filteredMenu.value) {
    if (!mod.children?.length) {
      if (mod.title.toLowerCase().includes(termo))
        resultado.push({ ...mod, _match: true, _matchBusca: false })
      continue
    }
    const filhos = mod.children.filter(c => c.title.toLowerCase().includes(termo))
    const modMatch = mod.title.toLowerCase().includes(termo)
    if (filhos.length) {
      resultado.push({
        ...mod,
        children: modMatch
          ? mod.children.map(c => ({ ...c, _match: c.title.toLowerCase().includes(termo) }))
          : filhos.map(c => ({ ...c, _match: true })),
        _matchBusca: true,
      })
    } else if (modMatch) {
      resultado.push({ ...mod, _matchBusca: true })
    }
  }
  return resultado
})

// Destaca trecho encontrado
function highlightText(texto, termo) {
  const escaped = termo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return texto.replace(new RegExp(`(${escaped})`, 'gi'), '<mark class="menu-highlight">$1</mark>')
}

</script>

<style scoped>
.menu-drawer {
  border-right: none;
  background-color: rgb(var(--v-theme-surface, 255, 255, 255)) !important;
  box-shadow: 1px 1px 2px -1px rgba(165, 165, 165, 0.7) !important;
  color: rgb(var(--v-theme-menu, 69, 85, 96)) !important;
}

.menu-list {
  padding-top: 10px;
  margin-top: 10px;
}

.v-list-item {
  min-height: 35px !important;
  padding: 4px 0 !important;
}

.v-list-item:hover {
  background-color: rgba(62, 121, 247, 0.05) !important;
  transition: background-color 0.2s;
}

.group-icon,
.menu-list .v-list-item .v-icon {
  font-size: 16px !important;
  color: rgb(var(--v-theme-menu, 69, 85, 96)) !important;
  opacity: 1 !important;
}

.menu-list .v-list-item-title {
  color: rgb(var(--v-theme-menu, 69, 85, 96)) !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  margin: 10px 0 !important;
}

/* Itens ativos */
.active-item {
  background-color: rgba(62, 121, 247, 0.1) !important;
  border-right: 3px solid rgb(var(--v-theme-primary, 51, 102, 255)) !important;
}

.active-item .v-list-item-title {
  color: rgb(var(--v-theme-primary, 51, 102, 255)) !important;
  font-weight: 600 !important;
}

/* Grupo ativo */
.active-group {
  background-color: rgba(62, 121, 247, 0.1) !important;
  border-right: 3px solid rgb(var(--v-theme-primary, 51, 102, 255)) !important;
}

.subitem {
  color: rgb(var(--v-theme-menu, 69, 85, 96)) !important;
  margin-left: 50px !important;
}

.active-item .bullet-marker {
  color: rgb(var(--v-theme-on-surface, 0, 0, 0)) !important;
  font-weight: bold;
}

.expand-icon.rotate {
  transform: rotate(180deg);
}

.v-list-item--nav {
  padding-inline: 12px !important;
}

.v-navigation-drawer--is-hovering {
  width: 400px !important;
}

.search-wrapper {
  transition: opacity 0.2s;
}

.search-field :deep(.v-field) {
  border-radius: 8px !important;
  font-size: 14px !important;
}

.search-field :deep(.v-field__input) {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
  padding-left: 30px !important;
  min-height: unset !important;
  font-size: 13px !important;
  color: rgb(var(--v-theme-menu, 69, 85, 96)) !important;
}

.search-field :deep(.v-field__outline) {
  color: rgb(255 255 255) !important;
}

:deep(.v-field) {
  background: #198cff24;
  padding: 5px 20px;
}

.search-icon {
  font-size: 15px !important;
  color: rgb(var(--v-theme-menu, 69, 85, 96)) !important;
  opacity: 0.6;
  margin-left: -6px;
}

.subitem-highlight {
  background-color: rgba(62, 121, 247, 0.04) !important;
}

:deep(.menu-highlight) {
  background-color: rgba(62, 121, 247, 0.2);
  color: rgb(var(--v-theme-primary, 51, 102, 255));
  border-radius: 2px;
  padding: 0 1px;
  font-weight: 700;
}
</style>
